import React, { Ref, useImperativeHandle } from "react";
import { View, StyleSheet } from "react-native";
import { useScreenshot } from "use-screenshot-hook";
import * as FileSaver from "file-saver";
import moment from "moment";
import { useDesktopMediaQuery } from "app/shared/util/media-query";
import { useReactToPrint } from "react-to-print";
import { Block } from "../blocks/block";
import { Caption, IconButton, Paragraph, useTheme } from "react-native-paper";
import { QueueSpacing } from "../spacing";

export interface GlobalViewShotProps {
  children: JSX.Element | JSX.Element[];
  watermark?: boolean;
}

export interface GlobalViewShotRef {
  takeViewShot: () => void;
  printViewShot: () => void;
  clearViewShot: () => void;
  getImageShot: () => void;
  statusViewShot: () => void;
}

const pageStyle = `
  @media all {
    .pagebreak {
      display: none;
    }
  }
  @page {
    size: auto;
  }

  @media print {
    @page { size: portrait; }
    .pagebreak {
      page-break-before: always;
    }
  }
`;

const GlobalViewShot = React.forwardRef((props: GlobalViewShotProps, ref: Ref<GlobalViewShotRef>) => {
  const { children, watermark = true } = props;
  const { colors, fonts } = useTheme();

  const viewshotRef = React.useRef<View>(null);
  const { image, takeScreenshot, isLoading, clear } = useScreenshot({ ref: viewshotRef });
  const isDesktop = useDesktopMediaQuery();
  useImperativeHandle(ref, () => {
    return { takeViewShot, printViewShot, getImageShot, clearViewShot, statusViewShot };
  });

  // Print
  const componentRef = React.useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  const takeViewShot = async () => {
    const _takePictures = await takeScreenshot();
    if (_takePictures) {
      const getFileName = moment().format("YYYYMMDDhhmmss");
      FileSaver.saveAs(`${_takePictures}`, `image_${getFileName}.jpg`);
      clear();
    }
  };

  const getImageShot = (): string | undefined => {
    return image;
  };

  const clearViewShot = () => {
    clear();
  };

  const statusViewShot = (): boolean => {
    console.log("Image pending !!!!");
    return isLoading;
  };

  const printViewShot = async () => {
    const _takePictures = await takeScreenshot();
    if (_takePictures) {
      handlePrint?.bind(this)();
      clear();
    }
  };
  const imagePrintStyleCss: React.CSSProperties = { width: "100%", height: "100%", objectFit: "contain" };
  const printContainer: React.CSSProperties = { display: "none" };

  const componentWithOverlay = (
    <View style={styles.overlayContainer}>
      <View ref={viewshotRef}>
        {watermark ? (
          <Block m={4} bg="white" borderColor={colors.primary} br={8} b={2}>
            <Block p={24}>
              <View style={styles.container} ref={viewshotRef}>
                {children}
              </View>
            </Block>
            <Block h={48} middle center>
              <Paragraph style={{ fontStyle: "italic", fontSize: 13, ...fonts.medium }}>Cảm ơn Quý khách đã sử dụng dịch vụ!</Paragraph>
            </Block>
            <Block bg={colors.primary} center row bottom h={36}>
              <IconButton style={{ backgroundColor: "white", borderRadius: 4 }} size={12} icon={"phone"} />
              <Caption style={{ color: "black", fontSize: 11 }}>1900 5555 92</Caption>
              <QueueSpacing size="medium" />
              <IconButton style={{ backgroundColor: "white", borderRadius: 4 }} size={12} icon={"web"} />
              <Caption style={{ color: "black", fontSize: 11 }}>PVcomBank.com.vn</Caption>
              <QueueSpacing size={isDesktop ? "huge" : "medium"} />
            </Block>
          </Block>
        ) : (
          <View style={styles.container}>{children}</View>
        )}
      </View>
    </View>
  );

  const ComponentToPrint = (
    <>
      <style>{pageStyle}</style>
      <div style={printContainer}>
        <div id="block-print" ref={componentRef}>
          <img src={image} style={imagePrintStyleCss} />
        </div>
      </div>
    </>
  );

  return (
    <>
      <View style={styles.container}>{children}</View>
      {ComponentToPrint}
      {componentWithOverlay}
    </>
  );
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "white",
  },
  overlayContainer: { position: "absolute", top: -9999 },
  imagePrint: {},
});

export default GlobalViewShot;
